<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import conversationWorkflowsAPI from 'dashboard/api/conversationWorkflows';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import FlowFilters from './FlowFilters.vue';
import { ATTENTION_HEALTH } from 'dashboard/helper/workflowHealth';
import FlowRow from './FlowRow.vue';
import CommentToDmWizard from './CommentToDmWizard.vue';

const router = useRouter();
const route = useRoute();
const { accountId } = useAccount();
const { t } = useI18n();

const flows = ref([]);
const isLoading = ref(true);
const loadError = ref(false);
const hasLoaded = ref(false);
const isCreating = ref(false);
const deletingId = ref(null);
const collapsedFolders = ref(new Set());

const search = ref('');
const channelFilter = ref('');
const statusFilter = ref('');
const folderFilter = ref('');

const NO_FOLDER_KEY = '__none__';
let loadSequence = 0;
let contextGeneration = 0;
let componentActive = true;

const isCurrentContext = (requestAccountId, requestGeneration) =>
  componentActive &&
  requestAccountId === accountId.value &&
  requestGeneration === contextGeneration;

const loadFlows = async () => {
  loadSequence += 1;
  const request = loadSequence;
  const requestAccountId = accountId.value;
  const requestGeneration = contextGeneration;
  isLoading.value = true;
  loadError.value = false;
  try {
    const { data } = await conversationWorkflowsAPI.get();
    if (
      request !== loadSequence ||
      !isCurrentContext(requestAccountId, requestGeneration)
    )
      return;
    flows.value = data?.payload ?? [];
    hasLoaded.value = true;
  } catch (error) {
    if (
      request !== loadSequence ||
      !isCurrentContext(requestAccountId, requestGeneration)
    )
      return;
    if ([401, 403, 404].includes(error.response?.status)) flows.value = [];
    useAlert(t('FLOW_AUTOMATIONS.INDEX.LOAD_ERROR'));
    loadError.value = true;
  } finally {
    if (
      request === loadSequence &&
      isCurrentContext(requestAccountId, requestGeneration)
    ) {
      isLoading.value = false;
    }
  }
};

const availableChannels = computed(() =>
  [...new Set(flows.value.flatMap(flow => flow.channels ?? []))].sort()
);

const availableFolders = computed(() =>
  [...new Set(flows.value.map(flow => flow.folder).filter(Boolean))].sort(
    (a, b) => a.localeCompare(b, 'pt-BR')
  )
);

const filteredFlows = computed(() => {
  const term = search.value.trim().toLowerCase();
  return flows.value.filter(flow => {
    if (term && !flow.name?.toLowerCase().includes(term)) return false;
    if (
      channelFilter.value &&
      !(flow.channels ?? []).includes(channelFilter.value)
    )
      return false;
    if (statusFilter.value === 'attention') {
      if (!ATTENTION_HEALTH.has(flow.health)) return false;
    } else if (statusFilter.value && flow.health !== statusFilter.value)
      return false;
    if (folderFilter.value && flow.folder !== folderFilter.value) return false;
    return true;
  });
});

// Pastas em ordem alfabética; "Sem pasta" sempre por último, nunca escondido.
const groups = computed(() => {
  const byFolder = new Map();
  filteredFlows.value.forEach(flow => {
    const key = flow.folder || NO_FOLDER_KEY;
    if (!byFolder.has(key)) byFolder.set(key, []);
    byFolder.get(key).push(flow);
  });

  const named = [...byFolder.entries()]
    .filter(([key]) => key !== NO_FOLDER_KEY)
    .sort(([a], [b]) => a.localeCompare(b, 'pt-BR'))
    .map(([key, items]) => ({ key, label: key, items }));

  const loose = byFolder.get(NO_FOLDER_KEY);
  return loose
    ? [
        ...named,
        {
          key: NO_FOLDER_KEY,
          label: t('FLOW_AUTOMATIONS.INDEX.NO_FOLDER'),
          items: loose,
        },
      ]
    : named;
});

const attentionCount = computed(
  () => flows.value.filter(flow => ATTENTION_HEALTH.has(flow.health)).length
);

const toggleFolder = key => {
  const next = new Set(collapsedFolders.value);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  collapsedFolders.value = next;
};

const openEditor = flow => {
  router.push({
    name: 'conversation_workflows_edit',
    params: { accountId: accountId.value, workflowId: String(flow.id) },
  });
};

// O assistente só monta quando a pessoa pede (v-if): a tela de lista não
// depende do seletor de posts nem das etiquetas.
const wizardMounted = ref(false);
const wizard = ref(null);

const openWizard = async () => {
  wizardMounted.value = true;
  await nextTick();
  wizard.value?.open();
};

const onWizardCreated = id => {
  if (id) openEditor({ id });
  else loadFlows();
};

const createFlow = async () => {
  const requestAccountId = accountId.value;
  const requestGeneration = contextGeneration;
  isCreating.value = true;
  try {
    const { data } = await conversationWorkflowsAPI.create({
      conversation_workflow: {
        name: t('FLOW_AUTOMATIONS.INDEX.DEFAULT_NAME'),
        description: '',
        // Nasce pausado: fluxo vazio abandonado ficava "ativo" na lista e o
        // listener passava a tratar a conta como tendo fluxo ativo. O editor
        // lembra de ligar quando o gatilho estiver montado.
        active: false,
        graph: { nodes: [], edges: [] },
      },
    });
    if (!isCurrentContext(requestAccountId, requestGeneration)) return;
    useAlert(t('FLOW_AUTOMATIONS.INDEX.CREATE_SUCCESS'));
    const id = data?.payload?.id;
    if (id) openEditor({ id });
    else await loadFlows();
  } catch {
    if (isCurrentContext(requestAccountId, requestGeneration)) {
      useAlert(t('FLOW_AUTOMATIONS.INDEX.CREATE_ERROR'));
    }
  } finally {
    if (isCurrentContext(requestAccountId, requestGeneration)) {
      isCreating.value = false;
    }
  }
};

// Confirmação no Dialog do produto, com o nome do fluxo: o confirm nativo
// não tinha tema escuro nem dizia o que ia ser apagado.
const deleteDialog = ref(null);
const flowToDelete = ref(null);

const requestDeleteFlow = flow => {
  flowToDelete.value = flow;
  deleteDialog.value?.open();
};

const confirmDeleteFlow = async () => {
  const flow = flowToDelete.value;
  if (!flow) return;
  await deleteFlow(flow);
  deleteDialog.value?.close();
  flowToDelete.value = null;
};

const deleteFlow = async flow => {
  const requestAccountId = accountId.value;
  const requestGeneration = contextGeneration;
  deletingId.value = flow.id;
  try {
    await conversationWorkflowsAPI.delete(flow.id);
    if (!isCurrentContext(requestAccountId, requestGeneration)) return;
    useAlert(t('FLOW_AUTOMATIONS.INDEX.DELETE_SUCCESS'));
    await loadFlows();
  } catch {
    if (isCurrentContext(requestAccountId, requestGeneration)) {
      useAlert(t('FLOW_AUTOMATIONS.INDEX.DELETE_ERROR'));
    }
  } finally {
    if (isCurrentContext(requestAccountId, requestGeneration)) {
      deletingId.value = null;
    }
  }
};

const newFolderDialog = ref(null);
const newFolderName = ref('');
const flowToMove = ref(null);

const moveFlow = async (flow, folder) => {
  if (folder === '__new__') {
    flowToMove.value = flow;
    newFolderName.value = '';
    newFolderDialog.value?.open();
    return;
  }
  if ((flow.folder ?? '') === folder) return;

  const requestAccountId = accountId.value;
  const requestGeneration = contextGeneration;
  try {
    await conversationWorkflowsAPI.update(flow.id, {
      conversation_workflow: { folder },
    });
    if (!isCurrentContext(requestAccountId, requestGeneration)) return;
    useAlert(t('FLOW_AUTOMATIONS.INDEX.MOVE_SUCCESS'));
    await loadFlows();
  } catch {
    if (isCurrentContext(requestAccountId, requestGeneration)) {
      useAlert(t('FLOW_AUTOMATIONS.INDEX.MOVE_ERROR'));
    }
  }
};

const confirmNewFolder = async () => {
  const folder = newFolderName.value.trim();
  const flow = flowToMove.value;
  if (!folder || !flow) return;
  newFolderDialog.value?.close();
  flowToMove.value = null;
  await moveFlow(flow, folder);
};

watch(accountId, (nextAccountId, previousAccountId) => {
  if (!nextAccountId || nextAccountId === previousAccountId) return;
  contextGeneration += 1;
  loadSequence += 1;
  flows.value = [];
  hasLoaded.value = false;
  loadError.value = false;
  isCreating.value = false;
  deletingId.value = null;
  search.value = '';
  channelFilter.value = '';
  statusFilter.value = '';
  folderFilter.value = '';
  collapsedFolders.value = new Set();
  loadFlows();
});

// SiteUp (Hub): a tela de início de Automações chega com ?new=1 (cria um
// fluxo vazio e abre o editor) ou ?wizard=comment_to_dm (abre o assistente).
// A query é consumida na chegada, para o F5 não repetir a ação.
const consumeEntryQuery = () => {
  const wantsNew = String(route.query?.new || '') === '1';
  const wantsWizard = route.query?.wizard === 'comment_to_dm';
  if (!wantsNew && !wantsWizard) return;
  const cleaned = { ...route.query };
  delete cleaned.new;
  delete cleaned.wizard;
  router.replace({ query: cleaned });
  if (wantsWizard) openWizard();
  else createFlow();
};

onMounted(() => {
  loadFlows();
  consumeEntryQuery();
});
onUnmounted(() => {
  componentActive = false;
  contextGeneration += 1;
  loadSequence += 1;
});
</script>

<template>
  <SettingsLayout
    data-testid="page-conversation-workflows"
    :no-records-found="false"
    class="gap-6"
  >
    <template #header>
      <div
        class="flex flex-col gap-4 w-full md:flex-row md:items-start md:justify-between"
      >
        <BaseSettingsHeader
          :title="$t('FLOW_AUTOMATIONS.INDEX.HEADER.TITLE')"
          :description="$t('FLOW_AUTOMATIONS.INDEX.HEADER.DESCRIPTION')"
          feature-name="conversation-workflows"
        />
        <div class="flex shrink-0 items-center gap-2">
          <Button
            :label="$t('FLOW_AUTOMATIONS.WIZARD.OPEN')"
            icon="i-ri-instagram-line"
            variant="faded"
            data-testid="open-comment-to-dm-wizard"
            @click="openWizard"
          />
          <Button
            :label="$t('FLOW_AUTOMATIONS.INDEX.NEW')"
            icon="i-ri-add-line"
            :is-loading="isCreating"
            @click="createFlow"
          />
        </div>
      </div>
      <CommentToDmWizard
        v-if="wizardMounted"
        ref="wizard"
        @created="onWizardCreated"
      />
    </template>

    <template #body>
      <div class="flex flex-col gap-4">
        <FlowFilters
          v-if="flows.length"
          v-model:search="search"
          v-model:channel="channelFilter"
          v-model:status="statusFilter"
          v-model:folder="folderFilter"
          :available-channels="availableChannels"
          :available-folders="availableFolders"
        />

        <button
          v-if="attentionCount && !statusFilter"
          type="button"
          class="flex items-center gap-2 rounded-xl border border-n-amber-6 bg-n-amber-2 px-4 py-2 text-sm text-n-amber-11 text-left"
          @click="statusFilter = 'attention'"
        >
          <span class="i-ph-warning size-4 shrink-0" aria-hidden="true" />
          <span>{{
            $t('FLOW_AUTOMATIONS.INDEX.ATTENTION_BANNER', {
              count: attentionCount,
            })
          }}</span>
        </button>

        <div
          v-if="isLoading && !hasLoaded"
          class="flex justify-center py-12 text-n-slate-11"
        >
          {{ $t('FLOW_AUTOMATIONS.INDEX.LOADING') }}
        </div>

        <div
          v-if="loadError"
          class="flex flex-col items-center gap-3 rounded-xl border border-n-ruby-6 bg-n-ruby-1 px-6 py-12 text-center text-n-ruby-11"
          role="alert"
        >
          <p class="m-0">{{ $t('FLOW_AUTOMATIONS.INDEX.LOAD_ERROR') }}</p>
          <Button
            :label="$t('FLOW_AUTOMATIONS.INDEX.RETRY')"
            variant="faded"
            :is-loading="isLoading"
            @click="loadFlows"
          />
        </div>

        <template v-if="!isLoading || hasLoaded">
          <div
            v-if="!flows.length && !loadError"
            data-testid="flow-empty"
            class="rounded-xl border border-n-weak bg-n-alpha-1 px-6 py-12 text-center text-n-slate-11"
          >
            {{ $t('FLOW_AUTOMATIONS.INDEX.EMPTY') }}
          </div>

          <div
            v-else-if="flows.length && !filteredFlows.length"
            class="rounded-xl border border-n-weak bg-n-alpha-1 px-6 py-12 text-center text-n-slate-11"
          >
            {{ $t('FLOW_AUTOMATIONS.INDEX.EMPTY_FILTERED') }}
          </div>

          <template v-else>
            <div v-for="group in groups" :key="group.key" class="flex flex-col">
              <button
                type="button"
                class="flex items-center gap-2 px-1 py-2 text-sm font-medium text-n-slate-11 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9 rounded-md"
                :aria-expanded="!collapsedFolders.has(group.key)"
                @click="toggleFolder(group.key)"
              >
                <span
                  aria-hidden="true"
                  class="i-ph-caret-right size-4 shrink-0 transition-transform"
                  :class="collapsedFolders.has(group.key) ? '' : 'rotate-90'"
                />
                <span>{{ group.label }}</span>
                <span class="text-n-slate-10">({{ group.items.length }})</span>
              </button>

              <div
                v-if="!collapsedFolders.has(group.key)"
                class="overflow-hidden rounded-xl border border-n-weak divide-y divide-n-weak"
              >
                <FlowRow
                  v-for="flow in group.items"
                  :key="flow.id"
                  :flow="flow"
                  :folders="availableFolders"
                  :is-deleting="deletingId === flow.id"
                  @open="openEditor"
                  @delete="requestDeleteFlow"
                  @move="moveFlow"
                />
              </div>
            </div>
          </template>
        </template>
      </div>
      <Dialog
        ref="deleteDialog"
        type="alert"
        :title="$t('FLOW_AUTOMATIONS.INDEX.DELETE_LABEL')"
        :description="
          $t('FLOW_AUTOMATIONS.INDEX.DELETE_CONFIRM_NAMED', {
            name: flowToDelete?.name || '',
          })
        "
        :confirm-button-label="$t('FLOW_AUTOMATIONS.INDEX.DELETE_LABEL')"
        :cancel-button-label="$t('FLOW_AUTOMATIONS.INDEX.CANCEL')"
        :is-loading="deletingId !== null"
        @confirm="confirmDeleteFlow"
        @close="flowToDelete = null"
      />
      <Dialog
        ref="newFolderDialog"
        :title="$t('FLOW_AUTOMATIONS.INDEX.NEW_FOLDER_TITLE')"
        :confirm-button-label="$t('FLOW_AUTOMATIONS.INDEX.NEW_FOLDER_CONFIRM')"
        :cancel-button-label="$t('FLOW_AUTOMATIONS.INDEX.CANCEL')"
        :disable-confirm-button="!newFolderName.trim()"
        @confirm="confirmNewFolder"
      >
        <label class="flex flex-col gap-1.5">
          <span class="text-sm text-n-slate-11">{{
            $t('FLOW_AUTOMATIONS.INDEX.NEW_FOLDER_PROMPT')
          }}</span>
          <input
            v-model="newFolderName"
            type="text"
            maxlength="80"
            class="reset-base box-border h-10 w-full rounded-xl border border-n-weak bg-n-solid-2 px-3 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
          />
        </label>
      </Dialog>
    </template>
  </SettingsLayout>
</template>
